import { IntlProvider } from 'react-intl'
import { localeTranslations } from './translation-files'
import { searchMfeLocaleTranslations } from './search-mfe-translation-files'
import { addIdsToTranslations, mergeTranslations } from './translations'
import { FC, ReactNode, useEffect, useState } from 'react'
import { createIntl, createIntlCache } from '@formatjs/intl'

const LOCALE_EXCEPTIONS: Record<string, string> = {
  'de-at': 'at',
}

export const getLanguageFromLocale = (locale: string): string => {
  const normalizedLocale = locale?.toLowerCase()

  if (normalizedLocale && LOCALE_EXCEPTIONS[normalizedLocale]) {
    return LOCALE_EXCEPTIONS[normalizedLocale]
  }

  const [language] = locale?.split('-') || []
  return language?.toLowerCase() || 'en'
}

export const getMergedTranslations = (
  locale: string,
  contentfulTranslations: Record<string, string>,
): Record<string, string> => {
  const language = getLanguageFromLocale(locale)

  const jsonTranslations = localeTranslations[language]
  const defaultTranslations = localeTranslations.en
  const defaultSearchMfeTranslations = searchMfeLocaleTranslations.en
  const searchMfeTranslations = searchMfeLocaleTranslations[language]

  return mergeTranslations([
    defaultSearchMfeTranslations,
    searchMfeTranslations,
    defaultTranslations,
    jsonTranslations,
    contentfulTranslations,
  ])
}

export const MessagesProvider: FC<{
  locale?: string
  contentfulTranslations?: Record<string, string>
  children?: ReactNode
}> = ({ locale, children, contentfulTranslations = {} }) => {
  let messages = getMergedTranslations(locale, contentfulTranslations)
  const [isTranslationDebugActive, setIsTranslationDebugActive] =
    useState(false)
  useEffect(() => {
    const params = new URLSearchParams(window?.location.search)
    const isActive = params.get('isTranslationDebugActive')
    setIsTranslationDebugActive(isActive === 'true')
  }, [global?.window?.location.search])

  if (isTranslationDebugActive) {
    messages = addIdsToTranslations(messages)
  }

  return (
    <IntlProvider locale={locale} messages={messages}>
      {children}
    </IntlProvider>
  )
}

export const getTranslation = (
  //get translations from Node
  contentfulLocale: string,
  id: string,
  defaultMessage: string,
): string => {
  // This is optional but highly recommended
  // since it prevents memory leak
  const cache = createIntlCache()
  const locale = getLanguageFromLocale(contentfulLocale)

  const intl = createIntl(
    {
      // Locale of the application
      locale,
      // Locale of the fallback defaultMessage
      defaultLocale: 'en',
      messages: mergeTranslations([
        localeTranslations.en,
        localeTranslations[locale],
      ]),
    },
    cache,
  )

  return intl.formatMessage({
    // Matching ID as above
    id,
    // Default Message in English
    defaultMessage,
  })
}
